import { useCallback } from "react";
import type { GuidanceProduct, GuidancePurpose } from "./trainingPanelGuidance";

/** When purpose/product filters change, start a fresh ask thread so retrieval context stays aligned. */
export function useTrainingGuidanceFilterHandlers(params: {
  askThreadId: string | null;
  askThreadTurnsLength: number;
  setAskThreadId: (v: string | null) => void;
  setAskThreadTurns: (v: any) => void;
  setNextAskKind: (v: "new" | "followup") => void;
  setGuidanceNotice: (v: string | null) => void;
  setPurposeSelection: (v: GuidancePurpose | null) => void;
  setProductSelection: (v: GuidanceProduct | null) => void;
}) {
  const {
    askThreadId,
    askThreadTurnsLength,
    setAskThreadId,
    setAskThreadTurns,
    setNextAskKind,
    setGuidanceNotice,
    setPurposeSelection,
    setProductSelection,
  } = params;

  const bumpThreadOnGuidanceChange = useCallback(() => {
    if (!askThreadId && askThreadTurnsLength === 0) return;
    setAskThreadId(null);
    setAskThreadTurns([]);
    setNextAskKind("new");
    setGuidanceNotice("Guidance changed. Started a new thread to avoid cross-context carryover.");
  }, [askThreadId, askThreadTurnsLength, setAskThreadId, setAskThreadTurns, setNextAskKind, setGuidanceNotice]);

  const onPurposeChange = useCallback(
    (next: GuidancePurpose | "") => {
      setPurposeSelection(next ? next : null);
      bumpThreadOnGuidanceChange();
    },
    [setPurposeSelection, bumpThreadOnGuidanceChange]
  );

  const onProductChange = useCallback(
    (next: GuidanceProduct | "") => {
      setProductSelection(next ? next : null);
      bumpThreadOnGuidanceChange();
    },
    [setProductSelection, bumpThreadOnGuidanceChange]
  );

  return { onPurposeChange, onProductChange };
}
